import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, IconButton,
  TextField, CircularProgress, Divider, Tooltip, Chip,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  Autocomplete, Slider, Collapse, Switch, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SaveIcon from '@mui/icons-material/Save';
import LinkIcon from '@mui/icons-material/Link';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShuffleIcon            from '@mui/icons-material/Shuffle';
import TuneIcon               from '@mui/icons-material/Tune';
import UndoIcon               from '@mui/icons-material/Undo';
import ContentCopyIcon        from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateIcon  from '@mui/icons-material/AddPhotoAlternate';
import QrCode2Icon            from '@mui/icons-material/QrCode2';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import OpenInNewIcon          from '@mui/icons-material/OpenInNew';
import RestartAltIcon         from '@mui/icons-material/RestartAlt';
import VisibilityOffIcon      from '@mui/icons-material/VisibilityOff';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import PushPinIcon            from '@mui/icons-material/PushPin';
import GroupAddIcon           from '@mui/icons-material/GroupAdd';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { useAltLogo, useQRLogo } from '../../hooks/useQRLogo';
import LagbasLogo from '../../components/LagbasLogo';
import SlideRenderer, { DEFAULT_SLIDE_THEME } from './slides/SlideRenderer';
import { generatePitch, analyzeProjectForPitch } from './services/aiService';
import {
  createPitch, updatePitch, getPitch,
  loadBackgrounds, loadProjects, loadOneProject,
} from './services/pitchService';
import { storage } from '../../firebase/config';
import CollaboratorsDialog from './CollaboratorsDialog';
import { colors, fontFamily } from '../../theme/tikabTokens';

// ── Tunn scrollbar — sx-mixin ─────────────────────────────────────────────────
const thinScrollbar = {
  scrollbarWidth: 'thin',                          // Firefox
  scrollbarColor: 'rgba(255,255,255,0.18) transparent',
  '&::-webkit-scrollbar':       { width: 4, height: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255,255,255,0.18)',
    borderRadius: 2,
    '&:hover': { background: 'rgba(255,255,255,0.35)' },
  },
};

// ── Slide-typer ──────────────────────────────────────────────────────────────

const SLIDE_TYPES = [
  { id: 'hero',              label: 'Hero-sektion' },
  { id: 'hero-mirror',       label: 'Hero-sektion (spegelvänd)' },
  { id: 'cover',             label: 'Cover' },
  { id: 'agenda',            label: 'Agenda / Körschema' },
  { id: 'divider',           label: 'Sektion (vänster)' },
  { id: 'section',           label: 'Avsnitt (centrerat)' },
  { id: 'stats',             label: 'Siffror' },
  { id: 'process',           label: 'Process' },
  { id: 'bullets',           label: 'Punktlista' },
  { id: 'key-statements',    label: 'Nyckelbudskap' },
  { id: 'reference-single',  label: 'Referensprojekt (ett)' },
  { id: 'reference',         label: 'Referensprojekt (flera)' },
  { id: 'closing',           label: 'Avslutning' },
  { id: 'image',             label: 'Bild' },
];

const DEFAULT_SLIDES = {
  hero:            { type: 'hero', title: 'Rubrik', tagline: '', client: '', logoOffsetX: 0, logoOffsetY: 0, overlayOpacity: 50, overlayBlur: 0, textBlockWidth: 700, backgroundImage: { id: 'bg-dark-cube-bright' }, qrTarget: 'presentation', qrCustomUrl: '', speakerNotes: '' },
  'hero-mirror':   { type: 'hero-mirror', title: 'Rubrik', tagline: '', client: '', logoOffsetX: 0, logoOffsetY: 0, overlayOpacity: 50, overlayBlur: 0, textBlockWidth: 700, backgroundImage: { id: 'bg-dark-cube-bright' }, qrTarget: 'presentation', qrCustomUrl: '', speakerNotes: '' },
  cover:           { type: 'cover', title: 'Rubrik', tagline: '', client: '', date: '', presenters: [], backgroundImage: { id: 'bg-dark-cube-bright' }, speakerNotes: '' },
  divider:         { type: 'divider', title: 'Sektionsnamn', backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  section:         { type: 'section', title: 'Avsnitt', label: '', backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  stats:           { type: 'stats', label: 'TIKAB I SIFFROR', stats: [{ value: '1977', label: 'Grundat' }, { value: '40', label: 'Medarbetare' }], backgroundImage: { id: 'bg-dark-office' }, speakerNotes: '' },
  process:         { type: 'process', label: 'VÅR PROCESS', steps: [{ number: '01', title: 'FÖRSTÅ & DEFINIERA', subtitle: '' }, { number: '02', title: 'UTVECKLA & DESIGNA', subtitle: '' }, { number: '03', title: 'IMPLEMENTERA', subtitle: '' }], backgroundImage: { id: 'bg-dark-mixed' }, speakerNotes: '' },
  bullets:         { type: 'bullets', label: '', title: 'Rubrik', bullets: ['Punkt 1', 'Punkt 2', 'Punkt 3'], backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  'key-statements': { type: 'key-statements', statements: ['Lång senior erfarenhet', 'Kombinerad leverans', 'Ledande domänexpertis'], backgroundImage: { id: 'bg-light-cube' }, speakerNotes: '' },
  'reference-single': { type: 'reference-single', project: null, backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  reference:       { type: 'reference', label: 'Referensprojekt', title: '', projects: [], backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  agenda:          { type: 'agenda', label: 'AGENDA', title: 'Agenda', date: '', subtitle: '', items: [{ time: '09:00', title: 'Välkomna', description: '' }, { time: '09:15', title: 'Presentation', description: '' }, { time: '10:00', title: 'Dialog', description: '' }], backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
  closing:         { type: 'closing', label: 'Tack!', callToAction: 'Hur kan vi hjälpa ER?', backgroundImage: { id: 'bg-light-camera' }, speakerNotes: '' },
  image:           { type: 'image', mediaType: 'image', imageUrl: '', videoUrl: '', webVideoUrl: '', imageScale: 100, imageOffsetX: 0, imageOffsetY: 0, backgroundImage: { id: 'bg-dark-cube-dim' }, speakerNotes: '' },
};

// ── Sorterbart slide-kort (thumbnail) ────────────────────────────────────────

function SortableSlideThumb({ slide, index, selected, onSelect, onDelete, onDuplicate, onToggleHidden, backgrounds, slideTheme, altLogoUrl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slide._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const hidden = !!slide.hidden;

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {/* Draghandtag */}
      <Box {...listeners} {...attributes} sx={{ cursor: 'grab', color: '#bbb', display: 'flex', flexShrink: 0 }}>
        <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
      </Box>
      {/* Thumbnail */}
      <Box
        onClick={() => onSelect(index)}
        sx={{
          flex: 1, cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden',
          border: selected ? `2px solid ${colors.orange}` : '2px solid transparent',
          transition: 'border-color 0.15s, opacity 0.15s',
          opacity: hidden ? 0.35 : 1,
          '&:hover': { borderColor: selected ? colors.orange : '#aaa' },
        }}
      >
        <SlideRenderer slide={slide} backgrounds={backgrounds} slideTheme={slideTheme} altLogoUrl={altLogoUrl} />
      </Box>
      {/* Dölj/visa */}
      <Tooltip title={hidden ? 'Visa i presentation' : 'Dölj i presentation'}>
        <IconButton size="small" onClick={() => onToggleHidden(index)}
          sx={{ color: hidden ? colors.orange : '#bbb', '&:hover': { color: hidden ? '#c44d00' : '#eee' }, flexShrink: 0 }}>
          {hidden ? <VisibilityOffIcon sx={{ fontSize: '1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1rem' }} />}
        </IconButton>
      </Tooltip>
      {/* Duplicera */}
      <IconButton size="small" onClick={() => onDuplicate(index)}
        sx={{ color: '#bbb', '&:hover': { color: colors.orange }, flexShrink: 0 }}>
        <ContentCopyIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
      {/* Radera */}
      <IconButton size="small" onClick={() => onDelete(index)}
        sx={{ color: '#bbb', '&:hover': { color: '#d32f2f' }, flexShrink: 0 }}>
        <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Box>
  );
}

// ── Hjälpkomponenter för referensprojekt ─────────────────────────────────────

function ProjectPicker({ label, projects, value, onSelect }) {
  const selected = projects.find(p => p.id === value) || null;
  return (
    <Autocomplete
      size="small"
      options={projects}
      getOptionLabel={o => `${o.projectNumber} – ${o.name}`}
      value={selected}
      onChange={(_, val) => onSelect(val)}
      renderInput={params => <TextField {...params} label={label} />}
      renderOption={(props, o) => (
        <Box component="li" {...props}>
          <Typography variant="body2" fontWeight={600}>{o.projectNumber}</Typography>
          <Typography variant="caption" sx={{ ml: 1 }}>{o.name}</Typography>
        </Box>
      )}
    />
  );
}

function ProjectFields({ p, onChange, showClient = false, onAnalyze }) {
  const [analyzing, setAnalyzing] = useState(false);

  async function handleAnalyze() {
    setAnalyzing(true);
    try {
      const result = await onAnalyze(p);
      if (result) onChange({ ...p, ...result });
    } catch (err) {
      console.error('AI-analys misslyckades', err);
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField size="small" fullWidth label="Titel"
        value={p.title || ''} onChange={e => onChange({ ...p, title: e.target.value })} />
      {showClient && (
        <TextField size="small" fullWidth label="Kund"
          value={p.client || ''} onChange={e => onChange({ ...p, client: e.target.value })} />
      )}
      <TextField size="small" fullWidth label="Vår roll i uppdraget"
        value={p.role || ''} onChange={e => onChange({ ...p, role: e.target.value })} />
      <TextField size="small" fullWidth label="Om projektet" multiline minRows={2}
        value={p.description || ''} onChange={e => onChange({ ...p, description: e.target.value })} />
      <TextField size="small" fullWidth label="Vårt uppdrag" multiline minRows={2}
        value={p.ourAssignment || ''} onChange={e => onChange({ ...p, ourAssignment: e.target.value })} />
      {onAnalyze && (
        <Button size="small" variant="outlined" disabled={analyzing}
          startIcon={analyzing ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />}
          onClick={handleAnalyze}
          sx={{ fontSize: 11, color: colors.orange, borderColor: `${colors.orange}66`,
            '&:hover': { borderColor: colors.orange, bgcolor: `${colors.orange}0d` },
            alignSelf: 'flex-start' }}>
          {analyzing ? 'Analyserar…' : 'AI-kondensera för presentation'}
        </Button>
      )}
    </Box>
  );
}

// ── Sorterbara listobjekt i editorn ─────────────────────────────────────────

function SortableItem({ id, children, alignItems = 'center' }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      sx={{ display: 'flex', gap: 1, alignItems }}
    >
      <Box {...listeners} {...attributes}
        sx={{ cursor: 'grab', color: '#bbb', display: 'flex', flexShrink: 0, alignSelf: 'flex-start', pt: '7px' }}>
        <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
      </Box>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
}

// ── Textstorleksreglage (återanvändbar) ──────────────────────────────────────

function FontSizeSlider({ label, field, slide, onChange, min = 10, max = 60, defaultValue }) {
  const val = slide[field];
  function remove() {
    const next = { ...slide };
    delete next[field];
    onChange(next);
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {label} — {val ?? 'Auto'}
        </Typography>
        {val !== undefined && (
          <Button size="small" onClick={remove}
            sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem', lineHeight: 1.4 }}>
            Auto
          </Button>
        )}
      </Box>
      <Slider size="small" min={min} max={max} step={1}
        value={val ?? defaultValue}
        onChange={(_, v) => onChange({ ...slide, [field]: v })}
        sx={{ color: colors.orange }} />
    </Box>
  );
}

// ── Slide-fältsredigerare ────────────────────────────────────────────────────

function SlideFieldEditor({ slide, onChange, backgrounds, projects, onMediaUpload, uploadProgress, onShuffleBg, pitchContext }) {
  const type = slide.type;
  const innerSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function analyzeProject(p) {
    return analyzeProjectForPitch({ project: p, pitchContext: pitchContext || {} });
  }

  function setField(key, value) {
    onChange({ ...slide, [key]: value });
  }

  function setBackground(id) {
    onChange({ ...slide, backgroundImage: { id } });
  }

  // Mappa projekt-objekt från bibliotek till slide-data
  function mapProject(val) {
    return {
      uppdragId:     val.id,
      projectNumber: val.projectNumber || '',
      title:         val.name || '',
      client:        val.client || '',
      role:          val.role || '',
      description:   val.description || '',
      ourAssignment: val.ourAssignment || '',
      keywords:      val.keywords || [],
      budget:        val.budget || '',
      startDate:     val.startDate || '',
      endDate:       val.endDate || '',
      imageUrl:      val.imageUrl || '',
    };
  }

  // Synka ett projekt från källan (projects-listan)
  async function syncProject(uppdragId, applyFn) {
    const fresh = await loadOneProject(uppdragId);
    if (fresh) applyFn(mapProject(fresh));
  }

  const bgId = slide.backgroundImage?.id || '';

  // Slides med glasspanel — visar lokala transparens/blur-sliders
  const HAS_PANEL = ['bullets', 'key-statements', 'closing', 'reference-single'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {/* Slide-typ */}
      <FormControl size="small" fullWidth>
        <InputLabel>Typ</InputLabel>
        <Select value={type} label="Typ" onChange={e => {
          const def = DEFAULT_SLIDES[e.target.value] || {};
          onChange({ ...def, _id: slide._id, backgroundImage: slide.backgroundImage });
        }}>
          {SLIDE_TYPES.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
        </Select>
      </FormControl>

      {/* Bakgrundsbild */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Bakgrundsbild</InputLabel>
          <Select value={bgId} label="Bakgrundsbild" onChange={e => setBackground(e.target.value)}>
            <MenuItem value=""><em>Ingen</em></MenuItem>
            {[...backgrounds].sort((a, b) => a.id.localeCompare(b.id)).map(b => (
              <MenuItem key={b.id} value={b.id}>{b.id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Slumpa bakgrund">
          <IconButton size="small" onClick={onShuffleBg} sx={{ flexShrink: 0 }}>
            <ShuffleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* Ladda upp egen bakgrundsbild */}
        <Tooltip title="Ladda upp bakgrund">
          <Box component="label" sx={{ flexShrink: 0 }}>
            <IconButton size="small" component="span"
              disabled={uploadProgress > 0}
              sx={{ color: slide.customBgUrl ? colors.orange : 'text.secondary' }}>
              {uploadProgress > 0
                ? <CircularProgress size={16} />
                : <AddPhotoAlternateIcon fontSize="small" />}
            </IconButton>
            <input type="file" accept="image/*" hidden onChange={e => {
              const file = e.target.files?.[0];
              if (file) onMediaUpload(file, url => onChange({ ...slide, customBgUrl: url }));
            }} />
          </Box>
        </Tooltip>
      </Box>

      {/* Uppladdad bakgrund — visa + rensa */}
      {slide.customBgUrl && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={slide.customBgUrl} alt=""
            sx={{ height: 36, width: 64, objectFit: 'cover', borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>Uppladdad bakgrund</Typography>
          <Button size="small" color="error"
            onClick={() => { const { customBgUrl, ...rest } = slide; onChange(rest); }}>
            Ta bort
          </Button>
        </Box>
      )}

      {/* Panel transparens + blur — för slides med glasspanel */}
      {HAS_PANEL.includes(type) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
              Transparens <span style={{ opacity: 0.7 }}>{slide.panelOpacity !== undefined ? `${slide.panelOpacity}%` : 'Global'}</span>
            </Typography>
            <Slider size="small" min={0} max={100} step={2}
              value={slide.panelOpacity ?? 52}
              onChange={(_, v) => setField('panelOpacity', v)}
              sx={{ color: colors.orange, flex: 1 }} />
            <Tooltip title="Återställ till global">
              <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
                onClick={() => { const { panelOpacity, ...rest } = slide; onChange(rest); }}>
                <RestartAltIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
              Blur <span style={{ opacity: 0.7 }}>{slide.panelBlur !== undefined ? `${slide.panelBlur}px` : 'Global'}</span>
            </Typography>
            <Slider size="small" min={0} max={48} step={2}
              value={slide.panelBlur ?? 24}
              onChange={(_, v) => setField('panelBlur', v)}
              sx={{ color: colors.orange, flex: 1 }} />
            <Tooltip title="Återställ till global">
              <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
                onClick={() => { const { panelBlur, ...rest } = slide; onChange(rest); }}>
                <RestartAltIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Övertäckning — visas överst för hero-sliden */}
      {(type === 'hero' || type === 'hero-mirror') && (<>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Övertäckning opacitet: {slide.overlayOpacity ?? 50}%
          </Typography>
          <Slider
            size="small"
            value={slide.overlayOpacity ?? 50}
            min={0} max={100} step={5}
            onChange={(_, v) => setField('overlayOpacity', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Övertäckning blur: {slide.overlayBlur ?? 0}px
          </Typography>
          <Slider
            size="small"
            value={slide.overlayBlur ?? 0}
            min={0} max={24} step={1}
            onChange={(_, v) => setField('overlayBlur', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Övertäckning bredd: {slide.overlayWidth ?? 55}%
          </Typography>
          <Slider
            size="small"
            value={slide.overlayWidth ?? 55}
            min={20} max={100} step={5}
            onChange={(_, v) => setField('overlayWidth', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
      </>)}

      <Divider />

      {/* Hero-fält (delade mellan hero och hero-mirror) */}
      {(type === 'hero' || type === 'hero-mirror') && (<>
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
        <Box>
          <Typography variant="caption" color="text.secondary">
            Rubrikstorlek: {slide.titleFontSize ?? 'Auto'}px
          </Typography>
          <Slider
            size="small"
            value={slide.titleFontSize ?? (slide.title?.length > 40 ? 52 : 64)}
            min={24} max={100} step={2}
            onChange={(_, v) => setField('titleFontSize', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        <TextField label="Tagline" size="small" fullWidth value={slide.tagline || ''} onChange={e => setField('tagline', e.target.value)} />
        <TextField label="Kund" size="small" fullWidth value={slide.client || ''} onChange={e => setField('client', e.target.value)} />
        <Box>
          <Typography variant="caption" color="text.secondary">
            Textblockbredd: {slide.textBlockWidth ?? 700}px
          </Typography>
          <Slider
            size="small"
            value={slide.textBlockWidth ?? 700}
            min={200} max={1000} step={20}
            onChange={(_, v) => setField('textBlockWidth', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Logotyp offset X: {slide.logoOffsetX ?? 0}px
          </Typography>
          <Slider
            size="small"
            value={slide.logoOffsetX ?? 0}
            min={-400} max={400} step={4}
            onChange={(_, v) => setField('logoOffsetX', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Logotyp offset Y: {slide.logoOffsetY ?? 0}px
          </Typography>
          <Slider
            size="small"
            value={slide.logoOffsetY ?? 0}
            min={-300} max={300} step={4}
            onChange={(_, v) => setField('logoOffsetY', v)}
            sx={{ color: colors.orange }}
          />
        </Box>
        {/* QR-målval */}
        <Divider />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            QR-kod länk
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={slide.qrTarget || 'presentation'}
            exclusive
            onChange={(_, v) => { if (v) setField('qrTarget', v); }}
            sx={{ width: '100%' }}
          >
            <ToggleButton value="presentation" sx={{ flex: 1, fontSize: '0.7rem', textTransform: 'none' }}>
              Presentationen
            </ToggleButton>
            <ToggleButton value="custom" sx={{ flex: 1, fontSize: '0.7rem', textTransform: 'none' }}>
              Anpassad URL
            </ToggleButton>
          </ToggleButtonGroup>
          {(slide.qrTarget || 'presentation') === 'custom' && (
            <TextField
              size="small" fullWidth
              label="Anpassad URL"
              placeholder="https://..."
              value={slide.qrCustomUrl || ''}
              onChange={e => setField('qrCustomUrl', e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </>)}

      {/* Cover-fält */}
      {type === 'cover' && (<>
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
        <TextField label="Tagline / Kategori" size="small" fullWidth value={slide.tagline || ''} onChange={e => setField('tagline', e.target.value)} />
        <TextField label="Kund" size="small" fullWidth value={slide.client || ''} onChange={e => setField('client', e.target.value)} />
        <TextField label="Datum" size="small" fullWidth value={slide.date || ''} onChange={e => setField('date', e.target.value)} placeholder="t.ex. April 2025" />
        {/* Presentatörer */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>Presentatörer</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
            {(slide.presenters || []).map((name, i) => (
              <Chip key={i} label={name} size="small" onDelete={() =>
                setField('presenters', (slide.presenters || []).filter((_, j) => j !== i))}
                sx={{ fontFamily }} />
            ))}
          </Box>
          <TextField
            size="small" fullWidth placeholder="Lägg till namn + Enter"
            onKeyDown={e => {
              if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                e.preventDefault();
                setField('presenters', [...(slide.presenters || []), e.target.value.trim()]);
                e.target.value = '';
              }
            }}
          />
        </Box>
      </>)}

      {/* Divider-fält */}
      {type === 'divider' && (
        <TextField label="Sektionsrubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
      )}

      {/* Section-fält (centrerat avsnitt) */}
      {type === 'section' && (<>
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
        <TextField label="Etikett (visas ovanför rubriken)" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        {/* Bakgrundsdimma */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Bakgrundsdimma — {slide.overlayOpacity ?? 0}%
            </Typography>
            {(slide.overlayOpacity ?? 0) > 0 && (
              <Button size="small" onClick={() => setField('overlayOpacity', 0)}
                sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem' }}>↺</Button>
            )}
          </Box>
          <Slider size="small" min={0} max={90} step={5}
            value={slide.overlayOpacity ?? 0}
            onChange={(_, v) => setField('overlayOpacity', v)}
            sx={{ color: colors.orange }} />
        </Box>
        {/* Skylt bakom rubriken */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Skylt bakom rubrik — {slide.panelOpacity ?? 0}%
            </Typography>
            {(slide.panelOpacity ?? 0) > 0 && (
              <Button size="small" onClick={() => setField('panelOpacity', 0)}
                sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem' }}>↺</Button>
            )}
          </Box>
          <Slider size="small" min={0} max={90} step={5}
            value={slide.panelOpacity ?? 0}
            onChange={(_, v) => setField('panelOpacity', v)}
            sx={{ color: colors.orange }} />
        </Box>
      </>)}

      {/* Agenda-fält */}
      {type === 'agenda' && (<>
        <TextField label="Etikett (ovanför titel)" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
        <TextField label="Datum (visas under rubrik)" size="small" fullWidth value={slide.date || ''} placeholder="t.ex. 15 april 2026" onChange={e => setField('date', e.target.value)} />
        <TextField label="Undertext (valfritt)" size="small" fullWidth value={slide.subtitle || ''} onChange={e => setField('subtitle', e.target.value)} />
        <DndContext sensors={innerSensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setField('items', arrayMove(slide.items || [], Number(active.id), Number(over.id)));
          }}
        >
          <SortableContext items={(slide.items || []).map((_, i) => i)} strategy={verticalListSortingStrategy}>
            {(slide.items || []).map((item, i) => (
              <SortableItem key={i} id={i} alignItems="flex-start">
                <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-start', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                  <TextField label="Tid" size="small" value={item.time || ''} sx={{ width: 80 }}
                    placeholder="09:00"
                    onChange={e => { const arr = [...slide.items]; arr[i] = { ...item, time: e.target.value }; setField('items', arr); }} />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField label="Moment" size="small" fullWidth value={item.title || ''}
                      onChange={e => { const arr = [...slide.items]; arr[i] = { ...item, title: e.target.value }; setField('items', arr); }} />
                    <TextField label="Beskrivning (valfritt)" size="small" fullWidth value={item.description || ''}
                      onChange={e => { const arr = [...slide.items]; arr[i] = { ...item, description: e.target.value }; setField('items', arr); }} />
                  </Box>
                  <IconButton size="small" onClick={() => setField('items', slide.items.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setField('items', [...(slide.items || []), { time: '', title: '', description: '' }])}>
          Lägg till moment
        </Button>
      </>)}

      {/* Stats-fält */}
      {type === 'stats' && (<>
        <TextField label="Etikett (t.ex. TIKAB I SIFFROR)" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        {(slide.stats || []).map((s, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Värde" size="small" value={s.value} sx={{ width: 90 }}
              onChange={e => { const arr = [...slide.stats]; arr[i] = { ...s, value: e.target.value }; setField('stats', arr); }} />
            <TextField label="Enhet" size="small" value={s.unit || ''} sx={{ width: 72 }}
              placeholder="ST, ÅR…"
              onChange={e => { const arr = [...slide.stats]; arr[i] = { ...s, unit: e.target.value }; setField('stats', arr); }} />
            <TextField label="Etikett" size="small" sx={{ flex: 1 }} value={s.label}
              onChange={e => { const arr = [...slide.stats]; arr[i] = { ...s, label: e.target.value }; setField('stats', arr); }} />
            <IconButton size="small" onClick={() => setField('stats', slide.stats.filter((_, j) => j !== i))}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={() => setField('stats', [...(slide.stats || []), { value: '', unit: '', label: '' }])}>
          Lägg till siffra
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
            Avstånd <span style={{ opacity: 0.7 }}>{slide.statGap !== undefined ? `${slide.statGap}px` : 'Auto'}</span>
          </Typography>
          <Slider size="small" min={0} max={300} step={4}
            value={slide.statGap ?? 80}
            onChange={(_, v) => setField('statGap', v)}
            sx={{ color: colors.orange, flex: 1 }} />
          <Tooltip title="Återställ">
            <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
              onClick={() => { const { statGap, ...rest } = slide; onChange(rest); }}>
              <RestartAltIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </>)}

      {/* Process-fält */}
      {type === 'process' && (<>
        <TextField label="Etikett" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        <DndContext sensors={innerSensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setField('steps', arrayMove(slide.steps || [], Number(active.id), Number(over.id)));
          }}
        >
          <SortableContext items={(slide.steps || []).map((_, i) => i)} strategy={verticalListSortingStrategy}>
            {(slide.steps || []).map((s, i) => (
              <SortableItem key={i} id={i} alignItems="flex-start">
                <Box sx={{
                  flex: 1, display: 'flex', gap: 1, alignItems: 'flex-start',
                  border: '1px solid', borderColor: s.pinned ? colors.orange : 'divider',
                  borderRadius: 1.5, p: 1.5,
                }}>
                  <TextField label="Nr" size="small" value={s.number || ''} sx={{ width: 64 }}
                    onChange={e => { const arr = [...slide.steps]; arr[i] = { ...s, number: e.target.value }; setField('steps', arr); }} />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField label="Rubrik" size="small" fullWidth value={s.title || ''}
                      onChange={e => { const arr = [...slide.steps]; arr[i] = { ...s, title: e.target.value }; setField('steps', arr); }} />
                    <TextField label="Undertext (valfritt)" size="small" fullWidth value={s.subtitle || ''}
                      onChange={e => { const arr = [...slide.steps]; arr[i] = { ...s, subtitle: e.target.value }; setField('steps', arr); }} />
                  </Box>
                  <Tooltip title={s.pinned ? 'Ta bort accentfärg' : 'Accentfärg (markera steg)'}>
                    <IconButton size="small"
                      onClick={() => { const arr = [...slide.steps]; arr[i] = { ...s, pinned: !s.pinned }; setField('steps', arr); }}
                      sx={{ color: s.pinned ? colors.orange : '#bbb', '&:hover': { color: colors.orange } }}>
                      <PushPinIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setField('steps', slide.steps.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setField('steps', [...(slide.steps || []), { number: '', title: '', subtitle: '' }])}>
          Lägg till steg
        </Button>
      </>)}

      {/* Bullets-fält */}
      {type === 'bullets' && (<>
        <TextField label="Sektionsetikett" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />
        <DndContext sensors={innerSensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const from = Number(active.id);
            const to   = Number(over.id);
            setField('bullets', arrayMove(slide.bullets || [], from, to));
          }}
        >
          <SortableContext items={(slide.bullets || []).map((_, i) => i)} strategy={verticalListSortingStrategy}>
            {(slide.bullets || []).map((b, i) => (
              <SortableItem key={i} id={i}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" sx={{ flex: 1 }} value={b}
                    onChange={e => { const arr = [...slide.bullets]; arr[i] = e.target.value; setField('bullets', arr); }} />
                  <IconButton size="small" onClick={() => setField('bullets', slide.bullets.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setField('bullets', [...(slide.bullets || []), ''])}>
          Lägg till punkt
        </Button>
      </>)}

      {/* Key-statements-fält */}
      {type === 'key-statements' && (<>
        <DndContext sensors={innerSensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setField('statements', arrayMove(slide.statements || [], Number(active.id), Number(over.id)));
          }}
        >
          <SortableContext items={(slide.statements || []).map((_, i) => i)} strategy={verticalListSortingStrategy}>
            {(slide.statements || []).map((s, i) => (
              <SortableItem key={i} id={i}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" sx={{ flex: 1 }} value={s}
                    onChange={e => { const arr = [...slide.statements]; arr[i] = e.target.value; setField('statements', arr); }} />
                  <IconButton size="small" onClick={() => setField('statements', slide.statements.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setField('statements', [...(slide.statements || []), ''])}>
          Lägg till påstående
        </Button>
      </>)}

      {/* Reference-single-fält */}
      {type === 'reference-single' && (<>
        <ProjectPicker
          label="Välj uppdrag"
          projects={projects}
          value={slide.project?.uppdragId || null}
          onSelect={val => setField('project', val ? mapProject(val) : null)}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            href="/reference-projects" target="_blank" rel="noopener noreferrer"
            sx={{ fontSize: 12, color: colors.orange, borderColor: colors.orange,
              '&:hover': { borderColor: colors.orange, bgcolor: 'rgba(255,120,0,0.06)' } }}>
            Redigera referensprojekt
          </Button>
          {slide.project?.uppdragId && (
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
              onClick={() => syncProject(slide.project.uppdragId, updated => setField('project', updated))}
              sx={{ fontSize: 12, color: 'text.secondary', borderColor: 'divider' }}>
              Synka data
            </Button>
          )}
        </Box>
        {slide.project && (
          <ProjectFields
            p={slide.project}
            onChange={updated => setField('project', updated)}
            showClient
            onAnalyze={analyzeProject}
          />
        )}
        {/* Antal chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
            Max chips <span style={{ opacity: 0.7 }}>{slide.maxChips ?? 8}</span>
          </Typography>
          <Slider size="small" min={0} max={15} step={1}
            value={slide.maxChips ?? 8}
            onChange={(_, v) => setField('maxChips', v)}
            sx={{ color: colors.orange, flex: 1 }} />
        </Box>
      </>)}

      {/* Reference-fält (flera) */}
      {type === 'reference' && (<>
        <TextField label="Etikett" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        <TextField label="Rubrik" size="small" fullWidth value={slide.title || ''} onChange={e => setField('title', e.target.value)} />

        {/* Mellanrum */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Mellanrum — {slide.cardGap ?? 12}px
          </Typography>
          <Slider size="small" min={4} max={48} step={2}
            value={slide.cardGap ?? 12}
            onChange={(_, v) => setField('cardGap', v)}
            sx={{ color: colors.orange }} />
        </Box>

        {/* Antal projekt */}
        <FormControl size="small" fullWidth>
          <InputLabel>Antal referensprojekt</InputLabel>
          <Select
            value={(slide.projects || []).length || 2}
            label="Antal referensprojekt"
            onChange={e => {
              const n = Number(e.target.value);
              const cur = slide.projects || [];
              setField('projects', Array.from({ length: n }, (_, i) => cur[i] ?? null));
            }}
          >
            {[2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n} projekt</MenuItem>)}
          </Select>
        </FormControl>

        {/* Chips-slider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
            Max chips <span style={{ opacity: 0.7 }}>{slide.maxChips ?? 'Auto'}</span>
          </Typography>
          <Slider size="small" min={0} max={10} step={1}
            value={slide.maxChips ?? 4}
            onChange={(_, v) => setField('maxChips', v)}
            sx={{ color: colors.orange, flex: 1 }} />
          <Tooltip title="Återställ">
            <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
              onClick={() => { const { maxChips, ...rest } = slide; onChange(rest); }}>
              <RestartAltIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Projekt — dragbar ordning */}
        <DndContext sensors={innerSensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setField('projects', arrayMove(slide.projects || [], Number(active.id), Number(over.id)));
          }}
        >
          <SortableContext items={(slide.projects || []).map((_, i) => i)} strategy={verticalListSortingStrategy}>
            {(slide.projects || [null, null]).map((p, i) => (
              <SortableItem key={i} id={i} alignItems="flex-start">
                <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ flex: 1, color: colors.orange, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      Projekt {i + 1}
                    </Typography>
                    {p?.uppdragId && (<>
                      <Tooltip title="Redigera referensprojekt">
                        <IconButton size="small" sx={{ p: 0.25 }} component="a"
                          href="/reference-projects" target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Synka data från referensprojekt">
                        <IconButton size="small" sx={{ p: 0.25 }}
                          onClick={() => syncProject(p.uppdragId, updated => {
                            const next = [...(slide.projects || [])];
                            next[i] = updated;
                            setField('projects', next);
                          })}>
                          <RestartAltIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>)}
                  </Box>
                  <ProjectPicker
                    label="Sök uppdrag"
                    projects={projects}
                    value={p?.uppdragId || null}
                    onSelect={val => {
                      const next = [...(slide.projects || [])];
                      next[i] = val ? mapProject(val) : null;
                      setField('projects', next);
                    }}
                  />
                  {p && (
                    <Box sx={{ mt: 1 }}>
                      <ProjectFields
                        p={p}
                        onChange={updated => {
                          const next = [...(slide.projects || [])];
                          next[i] = updated;
                          setField('projects', next);
                        }}
                        onAnalyze={analyzeProject}
                      />
                    </Box>
                  )}
                </Box>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
      </>)}

      {/* Closing-fält */}
      {type === 'closing' && (<>
        <TextField label="Etikett (t.ex. Tack!)" size="small" fullWidth value={slide.label || ''} onChange={e => setField('label', e.target.value)} />
        <TextField label="Call to action" size="small" fullWidth value={slide.callToAction || ''} onChange={e => setField('callToAction', e.target.value)} />
      </>)}

      {/* Bild / video-fält */}
      {type === 'image' && (<>
        {/* Mediatyp-väljare */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[
            { v: 'image', l: 'Bild' },
            { v: 'video', l: 'Video' },
            { v: 'embed', l: 'Webblänk' },
          ].map(({ v, l }) => (
            <Chip key={v} label={l} size="small" onClick={() => setField('mediaType', v)}
              sx={{
                cursor: 'pointer', fontFamily,
                bgcolor: (slide.mediaType || 'image') === v ? colors.orange : 'action.hover',
                color:   (slide.mediaType || 'image') === v ? 'white' : 'text.secondary',
                '&:hover': { opacity: 0.85 },
              }} />
          ))}
        </Box>

        {/* Bild */}
        {(slide.mediaType || 'image') === 'image' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField size="small" sx={{ flex: 1 }} placeholder="Klistra in URL…"
                value={slide.imageUrl || ''}
                onChange={e => setField('imageUrl', e.target.value)} />
              <Button component="label" size="small" variant="outlined"
                startIcon={uploadProgress > 0 ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
                disabled={uploadProgress > 0}
                sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                {uploadProgress > 0 ? `${uploadProgress}%` : 'Ladda upp'}
                <input type="file" accept="image/*" hidden onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) onMediaUpload(file, url => setField('imageUrl', url));
                }} />
              </Button>
            </Box>
            {slide.imageUrl && (
              <Box component="img" src={slide.imageUrl} alt=""
                sx={{ mt: 1, maxWidth: '100%', maxHeight: 72, objectFit: 'contain', borderRadius: 1, display: 'block' }} />
            )}
          </Box>
        )}

        {/* Uppladdad video */}
        {slide.mediaType === 'video' && (
          <Box>
            {slide.videoUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', wordBreak: 'break-all' }}>
                  {slide.videoUrl.split('/').pop()?.slice(0, 40)}
                </Typography>
                <Button size="small" color="error" onClick={() => setField('videoUrl', '')}>Ta bort</Button>
              </Box>
            ) : (
              <Button component="label" size="small" variant="outlined"
                startIcon={uploadProgress > 0 ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
                disabled={uploadProgress > 0}
                sx={{ fontSize: '0.75rem' }}>
                {uploadProgress > 0 ? `${uploadProgress}%` : 'Ladda upp video…'}
                <input type="file" accept="video/*" hidden onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) onMediaUpload(file, url => setField('videoUrl', url));
                }} />
              </Button>
            )}
          </Box>
        )}

        {/* Webblänk — YouTube / Vimeo */}
        {slide.mediaType === 'embed' && (
          <Box>
            <TextField size="small" fullWidth
              placeholder="https://www.youtube.com/watch?v=… eller Vimeo-länk"
              value={slide.webVideoUrl || ''}
              onChange={e => setField('webVideoUrl', e.target.value)} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              YouTube och Vimeo stöds. Videon spelas upp automatiskt utan ljud.
            </Typography>
          </Box>
        )}

        {/* Skala + förskjutning */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, flexShrink: 0 }}>
            Skala <span style={{ opacity: 0.7 }}>{slide.imageScale ?? 100}%</span>
          </Typography>
          <Slider size="small" min={10} max={300} step={5}
            value={slide.imageScale ?? 100}
            onChange={(_, v) => setField('imageScale', v)}
            sx={{ color: colors.orange, flex: 1 }} />
          <Tooltip title="Återställ">
            <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
              onClick={() => setField('imageScale', 100)}>
              <RestartAltIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, flexShrink: 0 }}>
            Offset X <span style={{ opacity: 0.7 }}>{slide.imageOffsetX ?? 0}px</span>
          </Typography>
          <Slider size="small" min={-600} max={600} step={4}
            value={slide.imageOffsetX ?? 0}
            onChange={(_, v) => setField('imageOffsetX', v)}
            sx={{ color: colors.orange, flex: 1 }} />
          <Tooltip title="Nolla">
            <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
              onClick={() => setField('imageOffsetX', 0)}>
              <RestartAltIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, flexShrink: 0 }}>
            Offset Y <span style={{ opacity: 0.7 }}>{slide.imageOffsetY ?? 0}px</span>
          </Typography>
          <Slider size="small" min={-360} max={360} step={4}
            value={slide.imageOffsetY ?? 0}
            onChange={(_, v) => setField('imageOffsetY', v)}
            sx={{ color: colors.orange, flex: 1 }} />
          <Tooltip title="Nolla">
            <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}
              onClick={() => setField('imageOffsetY', 0)}>
              <RestartAltIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </>)}

      {/* Talarnoter */}
      <Divider />
      <TextField
        label="Talarnoter"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={slide.speakerNotes || ''}
        onChange={e => setField('speakerNotes', e.target.value)}
        placeholder="Vad ska du säga på den här sliden?"
      />
    </Box>
  );
}

// ── Huvud-komponent ──────────────────────────────────────────────────────────

export default function PitchBuilder() {
  const { pitchId } = useParams();
  const navigate    = useNavigate();
  const { currentUser, userProfile } = useAuth();
  // Ägarskap: ägaren (createdBy) och medredaktörer (collaborators) får spara;
  // bara ägaren ändrar medredaktörerna (firestore.rules, pitches)
  const [ownerId, setOwnerId] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const [collabOpen, setCollabOpen] = useState(false);
  const isNew = !pitchId || pitchId === 'new';

  const [title,       setTitle]       = useState('');
  const [client,      setClient]      = useState('');
  const [slides,      setSlides]      = useState([]);
  const [selIdx,      setSelIdx]      = useState(0);
  const [backgrounds, setBackgrounds] = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [shareToken,  setShareToken]  = useState('');
  const [slideTheme,  setSlideTheme]  = useState(DEFAULT_SLIDE_THEME);
  const [excludedBgs, setExcludedBgs] = useState(new Set()); // bakgrunds-ID:n som exkluderas vid slumpning
  const [bgPickerOpen, setBgPickerOpen] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [aiError,     setAiError]     = useState('');
  const [snack,       setSnack]       = useState('');
  const [aiInput,     setAiInput]     = useState('');
  const [aiOpen,      setAiOpen]      = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copied,      setCopied]      = useState(false);
  const [slideListW,     setSlideListW]     = useState(240); // px — bredd på slide-listan
  const [formWidth,      setFormWidth]      = useState(320);
  const [bottomPanelH,   setBottomPanelH]   = useState(220); // px — höjd på tema/kontroll-panelen
  const dragState = useRef(null); // { startX, startW } or { startY, startH }

  const altLogoUrl = useAltLogo();
  const qrLogoUrl  = useQRLogo();
  const sensors   = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const prevSlides = useRef(null); // ett steg undo

  // ── Kolumnbredd-dragning (horisontell) ──
  const onDividerMouseDown = (e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startW: formWidth };
    const onMove = (ev) => {
      const delta = ev.clientX - dragState.current.startX;
      setFormWidth(Math.max(200, Math.min(600, dragState.current.startW + delta)));
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Slidelistbredd-dragning (horisontell, vänster kant) ──
  const onSlideListDividerMouseDown = (e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startW: slideListW };
    const onMove = (ev) => {
      const delta = ev.clientX - dragState.current.startX;
      setSlideListW(Math.max(140, Math.min(380, dragState.current.startW + delta)));
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Previewhöjd-dragning (vertikal) ──
  const onRowDividerMouseDown = (e) => {
    e.preventDefault();
    dragState.current = { startY: e.clientY, startH: bottomPanelH };
    const onMove = (ev) => {
      const delta = dragState.current.startY - ev.clientY; // drag upp → öka panelen
      setBottomPanelH(Math.max(80, Math.min(520, dragState.current.startH + delta)));
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Ladda initial data ──

  useEffect(() => {
    Promise.all([loadBackgrounds(), loadProjects()]).then(([bgs, projs]) => {
      setBackgrounds(bgs);
      setProjects(projs);
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    getPitch(pitchId).then(data => {
      if (!data) return;
      setTitle(data.title || '');
      setClient(data.client || '');
      setOwnerId(data.createdBy || '');
      setCollaborators(data.collaborators || []);
      setShareToken(data.shareToken || '');
      setSlides((data.slides || []).map((s, i) => ({ ...s, _id: s._id || String(i) })));
      setAiInput(data.userPrompt || '');
      if (data.slideTheme) setSlideTheme(data.slideTheme);
    });
  }, [pitchId, isNew]);

  // ── Hjälpare ──

  function withIds(arr) {
    return arr.map((s, i) => ({ ...s, _id: s._id || String(Date.now()) + i }));
  }

  function saveUndo(current) {
    prevSlides.current = current;
  }

  function undo() {
    if (!prevSlides.current) return;
    setSlides(prevSlides.current);
    prevSlides.current = null;
    setSnack('Ångrat');
  }

  // Ctrl+Z
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        // Undvik att ångra i textfält
        if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addSlide(type = 'divider') {
    saveUndo(slides);
    const def = { ...DEFAULT_SLIDES[type], _id: String(Date.now()) };
    setSlides(prev => {
      const next = [...prev];
      next.splice(selIdx + 1, 0, def);
      return next;
    });
    setSelIdx(selIdx + 1);
  }

  function deleteSlide(idx) {
    saveUndo(slides);
    setSlides(prev => prev.filter((_, i) => i !== idx));
    setSelIdx(s => Math.min(s, slides.length - 2));
  }

  function toggleHidden(idx) {
    setSlides(prev => prev.map((s, i) => i === idx ? { ...s, hidden: !s.hidden } : s));
  }

  function duplicateSlide(idx) {
    saveUndo(slides);
    const copy = { ...slides[idx], _id: String(Date.now()) };
    setSlides(prev => {
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setSelIdx(idx + 1);
  }

  function updateSlide(idx, updated) {
    setSlides(prev => {
      saveUndo(prev);
      return prev.map((s, i) => i === idx ? updated : s);
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlides(prev => {
      saveUndo(prev);
      const from = prev.findIndex(s => s._id === active.id);
      const to   = prev.findIndex(s => s._id === over.id);
      const next = arrayMove(prev, from, to);
      if (selIdx === from) setSelIdx(to);
      return next;
    });
  }

  // ── Bilduppladdning (video/bild) ──

  async function handleMediaUpload(file, onUrl) {
    setUploadProgress(1);
    const path = `pitches/media/${Date.now()}_${file.name}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file);
    task.on('state_changed',
      snap => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      () => { setUploadProgress(0); setSnack('Uppladdning misslyckades'); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onUrl(url);
        setUploadProgress(0);
      }
    );
  }

  // ── Slumpa bakgrund ──

  function randomBgId(currentId) {
    const pool = backgrounds.filter(b => b.url && !excludedBgs.has(b.id));
    if (!pool.length) return currentId;
    const others = pool.filter(b => b.id !== currentId);
    const pick   = others.length ? others : pool;
    return pick[Math.floor(Math.random() * pick.length)].id;
  }

  function shuffleOneBg(idx) {
    setSlides(prev => prev.map((s, i) => i === idx
      ? { ...s, backgroundImage: { id: randomBgId(s.backgroundImage?.id) } }
      : s));
  }

  // ── AI-generering ──

  async function handleGenerate() {
    if (!aiInput.trim()) return;
    setGenerating(true);
    setAiError('');
    try {
      const result = await generatePitch({
        userPrompt:  aiInput,
        projects,
        backgrounds,
      });
      if (!title && result.title)           setTitle(result.title);
      if (!client && result.suggestedClient) setClient(result.suggestedClient);
      // Slumpa bakgrunder från tillgänglig (icke-exkluderad) pool
      const pool = backgrounds.filter(b => b.url && !excludedBgs.has(b.id));
      const generatedSlides = (result.slides || []).map(s => {
        if (!pool.length) return s;
        const bg = pool[Math.floor(Math.random() * pool.length)];
        return { ...s, backgroundImage: { id: bg.id } };
      });
      setSlides(withIds(generatedSlides));
      setSelIdx(0);
      setAiOpen(false);
    } catch (e) {
      setAiError('Generering misslyckades. Kontrollera prompten och försök igen.');
    } finally {
      setGenerating(false);
    }
  }

  // ── Spara ──
  const isPitchOwner = !isNew && ownerId === currentUser?.uid;
  const canEditPitch = isNew || isPitchOwner || collaborators.includes(currentUser?.uid);

  async function handleSave() {
    if (!currentUser) return;
    setSaving(true);
    try {
      const payload = {
        title:      title || 'Namnlös pitch',
        client:     client || '',
        userPrompt: aiInput,
        slides:     slides.map(({ _id, ...s }) => s),
        slideTheme,
        altLogoUrl: altLogoUrl || '',
        qrLogoUrl:  qrLogoUrl  || '',
      };
      if (isNew) {
        const { id, shareToken: tok } = await createPitch({ ...payload, createdBy: currentUser.uid, departmentId: userProfile?.departmentId || '' });
        setShareToken(tok);
        navigate(`/pitch/${id}/edit`, { replace: true });
      } else {
        await updatePitch(pitchId, payload);
      }
      setSnack('Sparad!');
    } catch {
      setSnack('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  }

  function copyShareLink() {
    if (!shareToken) return;
    navigator.clipboard.writeText(`${window.location.origin}/p/${shareToken}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const selectedSlide = slides[selIdx];

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1, px: 2,
        display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <TextField
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Presentationstitel"
          variant="standard"
          InputProps={{ disableUnderline: true, sx: { color: 'white', fontWeight: 700, fontSize: '1rem', fontFamily } }}
          sx={{ flex: 1 }}
        />
        <TextField
          value={client}
          onChange={e => setClient(e.target.value)}
          placeholder="Kund"
          variant="standard"
          size="small"
          InputProps={{ disableUnderline: true, sx: { color: '#aaa', fontSize: '0.85rem', fontFamily } }}
          sx={{ width: 160 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          <Tooltip title="Ångra (Ctrl+Z)">
            <span>
              <IconButton size="small" color="inherit" onClick={undo} disabled={!prevSlides.current}>
                <UndoIcon sx={{ opacity: prevSlides.current ? 1 : 0.3 }} />
              </IconButton>
            </span>
          </Tooltip>
          {shareToken && (
            <Tooltip title={copied ? 'Kopierat!' : 'Kopiera delningslänk'}>
              <IconButton size="small" color="inherit" onClick={copyShareLink}>
                <LinkIcon sx={{ color: copied ? '#4caf50' : undefined }} />
              </IconButton>
            </Tooltip>
          )}
          {isPitchOwner && (
            <Tooltip title={collaborators.length ? `Medredaktörer (${collaborators.length})` : 'Medredaktörer'}>
              <IconButton size="small" color="inherit" onClick={() => setCollabOpen(true)}>
                <GroupAddIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isNew && (
            <Tooltip title="Presentera">
              <IconButton size="small" color="inherit" onClick={() => navigate(`/pitch/${pitchId}/present`)}>
                <PresentToAllIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={canEditPitch ? '' : 'Bara ägaren och medredaktörer kan spara pitchen'}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !canEditPitch}
                sx={{ bgcolor: colors.orange, '&:hover': { bgcolor: '#c44d00' }, fontFamily, ml: 0.5 }}
              >
                Spara
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* AI-panel (kollapsbar) */}
      <Paper square elevation={2} sx={{ px: 3, py: aiOpen ? 2 : 0.5, transition: 'padding 0.2s', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: aiOpen ? 1.5 : 0 }}
          onClick={() => setAiOpen(v => !v)}>
          <AutoAwesomeIcon sx={{ color: colors.orange, mr: 1 }} />
          <Typography fontWeight={700} sx={{ flex: 1, fontFamily }}>
            AI-generering
          </Typography>
          {aiOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        {aiOpen && (<>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            placeholder={`Beskriv mötet och klistra in allt innehåll här.\n\nExempel:\nVi träffar Polisen imorgon kl 10. De upphandlar digital byggprocess.\nVisa Rosenbad och Riksdagen som referensprojekt.\n\nTikab i siffror:\n1977: Grundat\n40: Medarbetare\n39: Snittålder\n50: Omsättning MSEK`}
            value={aiInput}
            onChange={e => setAiInput(e.target.value)}
            sx={{ mb: 1.5, '& textarea': { fontFamily: 'monospace' } }}
          />
          {aiError && <Alert severity="error" sx={{ mb: 1 }}>{aiError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerate}
              disabled={generating || !aiInput.trim()}
              sx={{ bgcolor: colors.orange, '&:hover': { bgcolor: '#c44d00' }, fontFamily }}
            >
              {generating ? 'Genererar…' : slides.length ? 'Regenerera' : 'Generera slides'}
            </Button>
            <Tooltip title="Ange mötesdiskussion, siffror, organiisationsstruktur eller projektnamn. AI väljer rätt slidetyp automatiskt.">
              <HelpOutlineIcon sx={{ fontSize: '1.1rem', color: '#aaa', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </>)}
      </Paper>

      {/* Huvud-yta: slide-lista vänster + preview+editor höger */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Vänster: slide-lista */}
        <Box sx={{
          width: slideListW, flexShrink: 0,
          overflowY: 'auto',
          p: 1.5,
          display: 'flex', flexDirection: 'column',
          ...thinScrollbar,
        }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={slides.map(s => s._id)} strategy={verticalListSortingStrategy}>
              {slides.map((slide, i) => (
                <SortableSlideThumb
                  key={slide._id}
                  slide={slide}
                  index={i}
                  selected={selIdx === i}
                  onSelect={setSelIdx}
                  onDelete={deleteSlide}
                  onDuplicate={duplicateSlide}
                  onToggleHidden={toggleHidden}
                  backgrounds={backgrounds}
                  slideTheme={slideTheme}
                  altLogoUrl={altLogoUrl}
                />
              ))}
            </SortableContext>
          </DndContext>
          {/* Lägg till slide */}
          <Box sx={{ mt: 1 }}>
            <Select
              size="small"
              displayEmpty
              value=""
              onChange={e => { if (e.target.value) addSlide(e.target.value); }}
              renderValue={() => '+ Lägg till slide'}
              sx={{ width: '100%', fontSize: '0.8rem' }}
            >
              {SLIDE_TYPES.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </Select>
          </Box>
        </Box>

        {/* Dragbart delare — slide-lista / formulär */}
        <Box
          onMouseDown={onSlideListDividerMouseDown}
          sx={{
            width: 5, flexShrink: 0, cursor: 'col-resize',
            bgcolor: 'divider',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: colors.orange },
            userSelect: 'none',
          }}
        />

        {/* Formulärkolumn */}
        <Box sx={{ width: formWidth, flexShrink: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', ...thinScrollbar }}>
          {selectedSlide ? (<>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {SLIDE_TYPES.find(t => t.id === selectedSlide.type)?.label || selectedSlide.type}
              </Typography>
            </Box>
            <SlideFieldEditor
              slide={selectedSlide}
              onChange={updated => updateSlide(selIdx, updated)}
              backgrounds={backgrounds}
              projects={projects}
              onMediaUpload={handleMediaUpload}
              uploadProgress={uploadProgress}
              onShuffleBg={() => shuffleOneBg(selIdx)}
              pitchContext={{ title, client, userPrompt: aiInput }}
            />
          </>) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography color="text.secondary" variant="body2" textAlign="center">
                Generera slides med AI eller lägg till manuellt
              </Typography>
            </Box>
          )}
        </Box>

        {/* Dragbart delare */}
        <Box
          onMouseDown={onDividerMouseDown}
          sx={{
            width: 5, flexShrink: 0, cursor: 'col-resize',
            bgcolor: 'divider',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: colors.orange },
            userSelect: 'none',
          }}
        />

        {/* Förhandsvisningskolumn */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selectedSlide && (<>
            {/* Preview — fyller tillgänglig höjd */}
            <Box sx={{ flex: 1, minHeight: 0, px: 2, pt: 2, pb: 0.5 }}>
              <SlideRenderer
                fitHeight
                slide={selectedSlide}
                backgrounds={backgrounds}
                slideTheme={slideTheme}
                altLogoUrl={altLogoUrl}
                qrLogoUrl={qrLogoUrl}
                qrUrl={slideTheme.showQr && shareToken
                  ? `${window.location.origin}/p/${shareToken}?slide=${selIdx}`
                  : undefined}
              />
            </Box>

            {/* Navigering */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.5, flexShrink: 0 }}>
              <IconButton size="small" onClick={() => setSelIdx(i => Math.max(0, i - 1))} disabled={selIdx === 0}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary">{selIdx + 1} / {slides.length}</Typography>
              <IconButton size="small" onClick={() => setSelIdx(i => Math.min(slides.length - 1, i + 1))} disabled={selIdx >= slides.length - 1}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Horisontell dragbar delare */}
            <Box
              onMouseDown={onRowDividerMouseDown}
              sx={{
                height: 5, flexShrink: 0, cursor: 'row-resize',
                bgcolor: 'divider',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: colors.orange },
                userSelect: 'none',
              }}
            />

            {/* Tema + anteckningar — scrollbar nederdel */}
            <Box sx={{
              overflowY: 'auto', px: 2, pb: 2,
              display: 'flex', flexDirection: 'column', gap: 2,
              flexShrink: 0, height: bottomPanelH,
              ...thinScrollbar,
            }}>

            {/* Presentationstema */}
            <Paper sx={{ borderRadius: 2, px: 2.5, py: 1.5 }} elevation={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, cursor: 'pointer' }}
                onClick={() => setBgPickerOpen(o => !o)}>
                <TuneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.1em', flex: 1 }}>
                  PRESENTATIONSTEMA
                </Typography>
                {bgPickerOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                {/* Panelstil */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Panel:</Typography>
                  {[{ v: 'glass', l: 'Glas' }, { v: 'opaque', l: 'Solid' }].map(({ v, l }) => (
                    <Chip key={v} label={l} size="small" onClick={() => setSlideTheme(t => ({ ...t, panelStyle: v }))}
                      sx={{
                        cursor: 'pointer', fontFamily,
                        bgcolor: slideTheme.panelStyle === v ? colors.orange : 'action.hover',
                        color:   slideTheme.panelStyle === v ? 'white' : 'text.secondary',
                        '&:hover': { bgcolor: slideTheme.panelStyle === v ? '#c44d00' : 'action.selected' },
                      }} />
                  ))}
                </Box>
                {/* Hörnradie */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Hörn:</Typography>
                  {[{ v: 0, l: '90°' }, { v: 8, l: 'S' }, { v: 14, l: 'M' }, { v: 24, l: 'L' }].map(({ v, l }) => (
                    <Chip key={v} label={l} size="small" onClick={() => setSlideTheme(t => ({ ...t, borderRadius: v }))}
                      sx={{
                        cursor: 'pointer', fontFamily, minWidth: 36,
                        bgcolor: slideTheme.borderRadius === v ? colors.orange : 'action.hover',
                        color:   slideTheme.borderRadius === v ? 'white' : 'text.secondary',
                        '&:hover': { bgcolor: slideTheme.borderRadius === v ? '#c44d00' : 'action.selected' },
                      }} />
                  ))}
                </Box>
                {/* QR-kod */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <QrCode2Icon sx={{ fontSize: 16, color: slideTheme.showQr ? colors.orange : 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">QR:</Typography>
                  <Switch
                    size="small"
                    checked={!!slideTheme.showQr}
                    onChange={e => setSlideTheme(t => ({ ...t, showQr: e.target.checked }))}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.orange },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.orange } }}
                  />
                  {slideTheme.showQr && !shareToken && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      Spara först
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* Opacity + Blur sliders */}
              <Collapse in={bgPickerOpen}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                  {/* Transparens */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
                      Transparens <span style={{ color: 'inherit', opacity: 0.7 }}>{slideTheme.panelOpacity ?? 52}%</span>
                    </Typography>
                    <Slider size="small" min={0} max={100} step={2}
                      value={slideTheme.panelOpacity ?? 52}
                      onChange={(_, v) => setSlideTheme(t => ({ ...t, panelOpacity: v }))}
                      sx={{ color: colors.orange, flex: 1 }} />
                    <Tooltip title="Återställ">
                      <IconButton size="small" sx={{ p: 0.25 }}
                        onClick={() => setSlideTheme(t => ({ ...t, panelOpacity: DEFAULT_SLIDE_THEME.panelOpacity }))}>
                        <RestartAltIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {/* Blur */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
                      Blur <span style={{ color: 'inherit', opacity: 0.7 }}>{slideTheme.panelBlur ?? 24}px</span>
                    </Typography>
                    <Slider size="small" min={0} max={48} step={2}
                      value={slideTheme.panelBlur ?? 24}
                      onChange={(_, v) => setSlideTheme(t => ({ ...t, panelBlur: v }))}
                      sx={{ color: colors.orange, flex: 1 }}
                      disabled={slideTheme.panelStyle === 'opaque'} />
                    <Tooltip title="Återställ">
                      <IconButton size="small" sx={{ p: 0.25 }}
                        onClick={() => setSlideTheme(t => ({ ...t, panelBlur: DEFAULT_SLIDE_THEME.panelBlur }))}>
                        <RestartAltIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {/* Referensuppdrag — synlighet */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                    Referensuppdrag — visa sektioner
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {[
                      { key: 'refShowOurAssignment', label: 'Vårt uppdrag' },
                      { key: 'refShowDescription',   label: 'Om projektet' },
                      { key: 'refShowRole',          label: 'Vår roll' },
                      { key: 'refShowChips',         label: 'Nyckelord' },
                      { key: 'refShowBudget',        label: 'Budget' },
                      { key: 'refShowTime',          label: 'Tidsperiod' },
                    ].map(({ key, label }) => {
                      const on = slideTheme[key] ?? DEFAULT_SLIDE_THEME[key];
                      return (
                        <Chip key={key} label={label} size="small"
                          onClick={() => setSlideTheme(t => ({ ...t, [key]: !on }))}
                          sx={{
                            cursor: 'pointer', fontFamily,
                            bgcolor: on ? colors.orange : 'action.hover',
                            color:   on ? 'white' : 'text.secondary',
                            '&:hover': { opacity: 0.85 },
                          }} />
                      );
                    })}
                  </Box>
                </Box>

                {/* Exkludera bakgrunder från slumpning */}
                {backgrounds.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                      Bakgrunder att slumpa bland (avmarkera för att utesluta)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {[...backgrounds].sort((a, b) => a.id.localeCompare(b.id)).map(bg => {
                        const excluded = excludedBgs.has(bg.id);
                        return (
                          <Chip
                            key={bg.id}
                            label={bg.id}
                            size="small"
                            onClick={() => setExcludedBgs(prev => {
                              const next = new Set(prev);
                              if (next.has(bg.id)) next.delete(bg.id);
                              else next.add(bg.id);
                              return next;
                            })}
                            sx={{
                              cursor: 'pointer',
                              fontFamily: 'monospace', fontSize: '0.68rem',
                              bgcolor: excluded ? 'action.disabledBackground' : 'action.hover',
                              color:   excluded ? 'text.disabled' : 'text.primary',
                              textDecoration: excluded ? 'line-through' : 'none',
                              '&:hover': { opacity: 0.8 },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Collapse>
            </Paper>

            {/* Textstorlekar per slide-typ */}
            {(() => {
              const s    = selectedSlide;
              const type = s.type;
              const upd  = updated => updateSlide(selIdx, updated);
              const configs = {
                cover:            [{ label: 'Kund / datum', field: 'fontSize',       min: 12, max: 48, def: 28 }],
                stats:            [{ label: 'Etiketttext',  field: 'fontSize',       min: 10, max: 36, def: 22 }],
                process:          [{ label: 'Stegtext',     field: 'fontSize',       min: 14, max: 60, def: 36 }],
                'key-statements': [{ label: 'Textstorlek',  field: 'fontSize',       min: 16, max: 72, def: 40 }],
                'reference-single': [{ label: 'Beskrivning', field: 'fontSize',      min: 10, max: 28, def: 18 }],
                reference:        [{ label: 'Kortstext',    field: 'fontSize',       min: 10, max: 28, def: 18 }],
                closing:          [{ label: 'CTA-text',     field: 'fontSize',       min: 20, max: 80, def: 56 }],
                bullets: [
                  { label: 'Rubrikstorlek', field: 'titleFontSize',  min: 16, max: 72,
                    def: (() => { const n=(s.bullets||[]).length; return n<=3?52:n<=4?44:n<=6?36:28; })() },
                  { label: 'Punktstorlek',  field: 'bulletFontSize', min: 12, max: 48,
                    def: (() => { const n=(s.bullets||[]).length; return n<=3?36:n<=4?30:n<=6?26:22; })() },
                ],
              };
              const cfgs = configs[type];
              if (!cfgs) return null;
              return (
                <Paper sx={{ borderRadius: 2, px: 2.5, py: 1.5 }} elevation={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.1em', display: 'block', mb: 1.5 }}>
                    TEXTSTORLEKAR
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {cfgs.map(cfg => (
                      <FontSizeSlider key={cfg.field} label={cfg.label} field={cfg.field}
                        slide={s} onChange={upd} min={cfg.min} max={cfg.max} defaultValue={cfg.def} />
                    ))}
                  </Box>
                </Paper>
              );
            })()}

            {/* Logo sidoläge */}
            <Paper sx={{ borderRadius: 2, px: 2.5, py: 1.5 }} elevation={1}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.1em', display: 'block', mb: 1 }}>
                ALTERNATIV LOGOTYP — POSITION
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
                  Höger indraget <span style={{ color: 'inherit', opacity: 0.7 }}>{slideTheme.logoPosX ?? 36}px</span>
                </Typography>
                <Slider size="small" min={4} max={600} step={2}
                  value={slideTheme.logoPosX ?? 36}
                  onChange={(_, v) => setSlideTheme(t => ({ ...t, logoPosX: v }))}
                  sx={{ color: colors.orange, flex: 1 }} />
                <Tooltip title="Återställ">
                  <IconButton size="small" sx={{ p: 0.25 }}
                    onClick={() => setSlideTheme(t => ({ ...t, logoPosX: DEFAULT_SLIDE_THEME.logoPosX }))}>
                    <RestartAltIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>

            </Box>{/* /scrollbar nederdel */}
          </>)}
        </Box>
      </Box>

      {isPitchOwner && (
        <CollaboratorsDialog
          open={collabOpen}
          onClose={() => setCollabOpen(false)}
          pitchId={pitchId}
          ownerId={ownerId}
          collaborators={collaborators}
          onSaved={ids => { setCollaborators(ids); setSnack('Medredaktörer sparade'); }}
        />
      )}

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
